<template>
  <b-sidebar
    id="new-certificate-type-sidebar"
    sidebar-class="sidebar-lg"
    :visible="newCertificateTypeSidebar.visible"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @shown="onShow"
    @change="val => mutateNewCertificateTypeSidebar({ visible: val })"
    @hidden="clear"
  >
    <template #default="{ hide }">

      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h4>Novo Curso</h4>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- Loading -->
      <div v-if="saving" class="text-center py-3">
        <b-spinner variant="primary" />
      </div>

      <!-- Form -->
      <b-form v-else class="p-2" @submit.prevent="onSubmit" @reset.prevent="resetForm">
        <b-row>

          <!-- Nome -->
          <b-col cols="12">
            <b-form-group label="Nome do curso" label-for="certificate-type-name" class="mt-1">
              <b-form-input
                id="certificate-type-name"
                v-model.trim="form.name"
                placeholder="Digite o nome do curso"
                :class="{ 'is-invalid': v$.form.name.$error }"
                @input="v$.form.name.$touch()"
              />

              <div class="invalid-feedback" :class="{ 'd-block': v$.form.name.$error }">
                <span v-if="v$.form.name.required.$invalid">Você deve informar o nome do curso.</span>
              </div>
            </b-form-group>
          </b-col>

          <!-- Carga horária -->
          <b-col cols="12">
            <b-form-group label="Carga horária total (horas)" label-for="certificate-type-workload" class="mt-1">
              <b-form-input
                id="certificate-type-workload"
                v-model.number="form.workload_hours"
                type="number"
                min="0"
                placeholder="Ex.: 252"
              />
              <small class="text-warning d-block mt-50">
                No certificado: "Com carga horária de
                <strong>{{ form.workload_hours || "XXX" }}</strong> horas no dia DD/MM/AAAA"
              </small>
            </b-form-group>
          </b-col>

          <!-- Selos institucionais -->
          <b-col cols="12">
            <b-form-group label="Selos institucionais" class="mt-1">
              <b-form-checkbox v-model="form.has_planejar_seal" class="mb-50">
                Selo Planejar pass
              </b-form-checkbox>

              <b-form-checkbox v-model="form.has_pec_ancord_seal">
                Selo PEC Ancord
              </b-form-checkbox>

              <small class="text-warning d-block mt-50">
                Só marque para cursos que realmente possuem a qualificação. Sem marcar, o certificado sai sem selo.
              </small>
            </b-form-group>
          </b-col>

          <!-- Descrição -->
          <b-col cols="12">
            <b-form-group label="Descrição" label-for="certificate-type-description" class="mt-1">
              <b-form-textarea
                id="certificate-type-description"
                v-model.trim="form.description"
                placeholder="(opcional)"
                rows="3"
              />
            </b-form-group>
          </b-col>

        </b-row>

        <!-- Actions -->
        <div class="d-flex mt-2 justify-content-between">

          <!-- Cancelar -->
          <b-button
            variant="outline-secondary"
            type="reset"
            :disabled="saving"
            class="certificate-buttons"
          >
            Cancelar
          </b-button>

          <!-- Criar curso -->
          <b-button
            variant="primary"
            type="submit"
            :disabled="saving"
            class="certificate-buttons"
          >
            {{ saving ? "Aguarde..." : "Criar curso" }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BCol,
  BRow,
  BSpinner
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as types from "../store/types";

const emptyForm = () => ({
  name: "",
  description: "",
  workload_hours: null,
  has_planejar_seal: false,
  has_pec_ancord_seal: false,
});

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BCol,
    BRow,
    BSpinner,
  },

  data() {
    return {
      saving: false,
      form: emptyForm()
    };
  },

  setup() {
    return {
      v$: useVuelidate(),
      toast: useToast(),
    };
  },

  validations() {
    return {
      form: {
        name: { required },
        description: {}
      }
    };
  },

  computed: {
    ...mapGetters({
      newCertificateTypeSidebar: types.NEW_CERTIFICATE_TYPE_SIDEBAR,
    }),
  },

  methods: {
    ...mapMutations({
      mutateNewCertificateTypeSidebar: types.MUTATE_NEW_CERTIFICATE_TYPE_SIDEBAR,
    }),

    ...mapActions({
      saveCertificateType: types.SAVE_CERTIFICATE_TYPE,
    }),

    onShow() {
      this.saving = false;
      this.form = emptyForm();
      this.v$.$reset();
    },

    clear() {
      this.form = emptyForm();
      this.v$.$reset();
      this.mutateNewCertificateTypeSidebar({
        visible: false
      });
    },

    resetForm() {
      this.clear();
    },

    async onSubmit() {
      const isValid = await this.v$.$validate();
      if (!isValid) return;

      this.saving = true;

      this.saveCertificateType(this.form)
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: "Curso criado com sucesso!",
              icon: "CheckIcon",
              variant: "success",
            },
          });

          this.clear();

          if (this.newCertificateTypeSidebar.saveAction) {
            this.newCertificateTypeSidebar.saveAction();
          }
        })
        .catch((error) => {
          let msg = "Não foi possível criar o curso.";

          if (error.response?.data?.name) {
            msg = "Não foi possível criar o curso, já existe um curso com esse nome.";
          }

          this.toast({
            component: ToastificationContent,
            props: {
              title: "Erro!",
              text: msg,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.certificate-buttons {
  width: 11rem;
}
</style>
